import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDialog } from '../context/DialogContext';
import useIsMobile from '../hooks/useIsMobile';
import { safeBottom } from '../utils/safeArea';
import { SurfacePage, SoftSurface } from '../components/surface';
import { LoadingSpinner, ErrorState } from '../components';
import ForumCategoriesPanel from '../components/ForumCategoriesPanel';
import ClubLocationField from '../components/ClubLocationField';
import clubService from '../services/clubService';
import clubMembershipService from '../services/clubMembershipService';
import { money, plural } from '../utils/surfaceFormat';
import ContactsPanel from '../components/audience/ContactsPanel';
import BroadcastsPanel, { useBroadcastTakeover } from '../components/audience/BroadcastsPanel';
import SendingFrom from '../components/broadcast/SendingFrom';
import audienceService from '../services/audienceService';
import { ClubLogo } from '../components/club/ClubCover';
import {
  ClubPanel, PanelHead, PillButton, ClubBackPill, RingTab, TagPill, Switch, SwitchRowSoft, Face, useArmed, useToast, Toast, ErrorBand,
  INK, MUTED, SUBTLE, BODY, INSET, CHIP, CYAN, YELLOW, RED, RULE, label as lbl, inputStyle, textareaStyle, voiceStyle,
} from '../components/club/clubUi';

/**
 * CLUB SETTINGS — design_handoff_club_pages/README.md §2 (`/clubs/:id/manage?tab=`).
 * Header card, tab pills (BASICS · WHO GETS IN · FEATURES · MEMBERSHIP · CONTACTS · SEND ·
 * DANGER), a 340px sticky rail whose WHAT MEMBERS SEE summary updates as the toggles flip,
 * SAVE CHANGES and PREVIEW AS MEMBER. CONTACTS and SEND are full-width with no rail and no save.
 *
 * Money copy is truthful (inventory/03 §3.6): the member pays the sticker price PLUS the Portal
 * fee, GST and card processing; the host is credited the full sticker price.
 */

const TAB_KEYS = ['basics', 'access', 'features', 'membership', 'people', 'send', 'danger'];
const TABS = [
  { key: 'basics', label: 'BASICS' },
  { key: 'access', label: 'WHO GETS IN' },
  { key: 'features', label: 'FEATURES' },
  { key: 'membership', label: 'MEMBERSHIP' },
  { key: 'people', label: 'CONTACTS' },
  { key: 'send', label: 'SEND' },
  { key: 'danger', label: 'DANGER', danger: true },
];
const PEOPLE_TABS = ['people', 'send'];
/** Owner + admins alike; the manage surface itself already needs one of the two. */
const PAGE_PERMS = { canEdit: true, canCreate: true, canSend: true };

const BILLING_MODES = [
  { key: 'free', label: 'FREE' },
  { key: 'monthly', label: 'MONTHLY' },
  { key: 'annual', label: 'ANNUAL' },
  { key: 'one_time', label: 'ONE-TIME' },
];

/** The four UI billing modes collapse onto the API's three shapes. */
export function apiShapeFor(uiMode) {
  if (uiMode === 'free') return 'free';
  if (uiMode === 'one_time') return 'one_time';
  return 'subscription';
}

function uiModeFromClub(club) {
  if (!club || !club.membershipMode || club.membershipMode === 'free') return 'free';
  if (club.membershipMode === 'one_time') return 'one_time';
  return club.membershipBillingPeriod === 'annual' ? 'annual' : 'monthly';
}

function formFromClub(data) {
  return {
    name: data.name || '',
    tagline: data.tagline || '',
    description: data.description || '',
    ritual: data.ritual || '',
    isPublic: data.isPublic !== false,
    requiresApproval: !!data.requiresApproval,
    anyoneCanInvite: !!data.anyoneCanInvite,
    maxMembers: data.maxMembers ? String(data.maxMembers) : '',
    memberSignalsEnabled: data.memberSignalsEnabled !== false,
    forumEnabled: !!data.forumEnabled,
    showMembersList: data.showMembersList !== false,
    gatheringsEnabled: data.gatheringsEnabled !== false,
  };
}

function membershipFromClub(data) {
  return {
    mode: uiModeFromClub(data),
    price: data.membershipPriceCents ? (data.membershipPriceCents / 100).toFixed(2) : '',
    currency: data.currency || 'CAD',
    term: data.membershipTermDays ? String(data.membershipTermDays) : '365',
    lifetime: data.membershipMode === 'one_time' && !data.membershipTermDays,
  };
}

function priceCentsOf(membership) {
  const n = parseFloat(membership.price || '0');
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

/** `$12.00 / MONTH` · `$40.00 ONCE` · `FREE` — the rail summary and the fee note share it. */
export function membershipLabel(membership) {
  if (membership.mode === 'free') return 'FREE';
  const cents = priceCentsOf(membership);
  if (membership.mode === 'one_time') return `${money(cents)} ONCE`;
  return `${money(cents)} / ${membership.mode === 'annual' ? 'YEAR' : 'MONTH'}`;
}

/** The truthful fee note, per mode (inventory/03 §3.6 — fees are additive, host keeps the sticker). */
export function feeNote(membership) {
  if (membership.mode === 'free') return "Nobody is charged to be a member. You can still sell tickets to the club's events.";
  const price = money(priceCentsOf(membership));
  const gst = membership.currency === 'CAD' ? ', 5% GST' : '';
  const tail = `plus the $1.00 Portal fee${gst} and card processing on top. The full ${price} lands in your wallet the day it clears.`;
  if (membership.mode === 'one_time') {
    const term = membership.lifetime ? 'for life' : `for ${plural(parseInt(membership.term, 10) || 0, 'day').toLowerCase()}`;
    return `Members pay ${price} once and stay in ${term}, ${tail}`;
  }
  return `Members pay ${price} ${membership.mode === 'annual' ? 'a year' : 'a month'} ${tail}`;
}

/** What the settings add up to for a member — the perks list is derived, there is no free-text field. */
export function memberPerks(form) {
  const perks = ['The feed and announcements', 'Member-only events and the club calendar'];
  if (form.memberSignalsEnabled) perks.push('Member Portals');
  if (form.forumEnabled) perks.push('The club forum');
  if (form.gatheringsEnabled) perks.push('Gatherings — whole weekends of events');
  if (form.showMembersList) perks.push('The members list');
  return perks;
}

export function summaryRows(form, membership, categoryCount) {
  const approval = form.isPublic ? (form.requiresApproval ? 'REQUIRED' : 'INSTANT') : 'BY REQUEST';
  return [
    ['Visibility', form.isPublic ? 'PUBLIC' : 'PRIVATE'],
    ['Approval', approval],
    ['Who can invite', form.anyoneCanInvite ? 'ANY MEMBER' : 'ADMINS ONLY'],
    ['Member Portals', form.memberSignalsEnabled ? 'ON' : 'OFF'],
    ['Club forum', form.forumEnabled ? (categoryCount != null ? `ON · ${plural(categoryCount, 'CATEGORY', 'CATEGORIES')}` : 'ON') : 'OFF'],
    ['Members list', form.showMembersList ? 'VISIBLE' : 'ADMINS ONLY'],
    ['Gatherings', form.gatheringsEnabled ? 'ON' : 'OFF'],
    ['Membership', membershipLabel(membership)],
    ['Member cap', form.maxMembers ? String(parseInt(form.maxMembers, 10)) : 'NONE'],
  ];
}

const fieldLabel = { ...lbl(10, '.16em') };

function FieldBlock({ label, required, counter, hint, children }) {
  return (
    <label className="flex flex-col" style={{ gap: 8 }}>
      <span className="flex justify-between" style={{ gap: 10 }}>
        <span style={fieldLabel}>{label}{required && <span style={{ color: '#FF00AA' }}> *</span>}</span>
        {counter && <span style={{ fontSize: 10, color: SUBTLE }}>{counter}</span>}
      </span>
      {children}
      {hint && <span style={{ fontSize: 11, lineHeight: 1.55, color: MUTED }}>{hint}</span>}
    </label>
  );
}

/** Summary value chip: black when the value is ON / ON · …, grey otherwise (handoff logic). */
function SummaryValue({ v }) {
  const on = v === 'ON' || v.startsWith('ON ·');
  return <TagPill bg={on ? INK : CHIP} fg={on ? '#fff' : INK} size={9} padding="4px 10px" style={{ letterSpacing: '.12em', flexShrink: 0 }}>{v}</TagPill>;
}

function WhatMembersSee({ rows, dirty, phone }) {
  return (
    <ClubPanel phone={phone} style={{ gap: 4 }} data-testid="what-members-see">
      <span className="flex justify-between" style={{ gap: 10, paddingBottom: 8 }}>
        <span style={lbl(12, '.16em')}>WHAT MEMBERS SEE</span>
        {dirty && <TagPill bg={YELLOW} fg={INK} data-testid="unsaved-tag">UNSAVED</TagPill>}
      </span>
      {rows.map(([k, v], i) => (
        <span key={k} className="flex items-center justify-between" style={{ gap: 12, padding: '8px 0', borderTop: i ? RULE : 'none' }}>
          <span style={{ fontSize: 12, color: MUTED }}>{k}</span>
          <SummaryValue v={v} />
        </span>
      ))}
    </ClubPanel>
  );
}

/** Image slot: 120px radius-18 preview, REPLACE / UPLOAD (or EDIT COVER →), REMOVE, UPLOADING… overlay. */
function ImageSlot({ label, imageUrl, fit = 'cover', uploading, onPick, onRemove, inputRef, onFile, testId, hint, replaceLabel, onReplaceLink }) {
  const has = !!imageUrl && !uploading;
  return (
    <div className="flex min-w-0 flex-col" style={{ gap: 8 }}>
      <span style={fieldLabel}>{label}</span>
      <div className="relative flex items-center justify-center overflow-hidden" style={{ height: 120, borderRadius: 18, background: INSET }}>
        {has && <img src={imageUrl} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: fit, display: 'block' }} />}
        {!has && !uploading && <span style={{ ...lbl(10, '.16em'), color: SUBTLE }}>NONE</span>}
        {uploading && <span className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(255,255,255,.85)', ...lbl(10, '.16em', 800) }}>UPLOADING…</span>}
      </div>
      <span className="flex flex-wrap" style={{ gap: 6 }}>
        {onReplaceLink
          ? <PillButton size={32} to={onReplaceLink} data-testid={testId}>{replaceLabel}</PillButton>
          : <PillButton size={32} onClick={onPick} disabled={uploading} aria-label={`${imageUrl ? 'Replace' : 'Upload'} ${label.toLowerCase()}`}>{imageUrl ? 'REPLACE' : 'UPLOAD'}</PillButton>}
        {has && onRemove && <PillButton variant="danger" size={32} onClick={onRemove} aria-label={`Remove ${label.toLowerCase()}`}>REMOVE</PillButton>}
      </span>
      {hint && <span style={{ fontSize: 10, lineHeight: 1.5, color: MUTED }}>{hint}</span>}
      {inputRef && <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={testId} />}
    </div>
  );
}

export default function ClubManagePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const { showAlert } = useDialog();
  const mobile = useIsMobile();
  const { armed, confirm, disarm } = useArmed(6000);
  const { toast, say } = useToast();

  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [form, setForm] = useState(null);
  const [initialForm, setInitialForm] = useState(null);
  const [membership, setMembership] = useState(null);
  const [initialMembership, setInitialMembership] = useState(null);

  // Home town lives beside the form: the backend writes global + city together, and it is
  // only sent once touched so an older, never-placed club isn't blocked on re-picking a town.
  const [isGlobal, setIsGlobal] = useState(true);
  const [homeCity, setHomeCity] = useState(null);
  const [locationTouched, setLocationTouched] = useState(false);

  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved
  const [saveError, setSaveError] = useState(null);
  const savedTimer = useRef(null);

  const avatarInputRef = useRef(null);
  const backgroundInputRef = useRef(null);
  const [uploading, setUploading] = useState({ avatar: false, background: false });

  const [categoryCount, setCategoryCount] = useState(null);

  const [transferOpen, setTransferOpen] = useState(false);
  const [admins, setAdmins] = useState(null);
  const [transferBusy, setTransferBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isOwner = club?.userRole === 'owner';
  const sendTakeover = useBroadcastTakeover();
  const audienceOwner = useMemo(() => (club ? { ownerType: 'club', ownerId: club.id, name: club.name, publicPath: `/clubs/${club.id}`, followerWord: 'MEMBERS', logoUrl: club.avatarUrl || null } : null), [club]);

  // EMAIL on BASICS: sending address, replies, own domain — the card the email builder carries.
  const emailClient = useMemo(() => (club?.id ? audienceService.broadcastClient('club', club.id) : null), [club?.id]);
  const tabs = useMemo(() => TABS.filter((t) => t.key !== 'danger' || isOwner), [isOwner]);
  const tabParam = params.get('tab');
  const tab = TAB_KEYS.includes(tabParam) && (tabParam !== 'danger' || isOwner) ? tabParam : 'basics';
  const setTab = useCallback((key) => {
    const next = new URLSearchParams(params);
    if (key === 'basics') next.delete('tab'); else next.set('tab', key);
    if (key !== 'send') { next.delete('pick'); next.delete('pickUsers'); next.delete('send'); next.delete('ch'); }
    setParams(next, { replace: true });
  }, [params, setParams]);

  // SEND TO THESE → from the contact list carries the picked ids on the URL.
  const initialPick = useMemo(() => {
    const c = params.get('pick'); const u = params.get('pickUsers');
    if (!c && !u) return null;
    return { contactIds: c ? c.split(',').filter(Boolean) : [], userIds: u ? u.split(',').filter(Boolean) : [] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get('pick'), params.get('pickUsers')]);
  const sendTo = useCallback(({ userIds, contactIds }) => {
    const next = new URLSearchParams(params);
    next.set('tab', 'send');
    if (contactIds?.length) next.set('pick', contactIds.join(',')); else next.delete('pick');
    if (userIds?.length) next.set('pickUsers', userIds.join(',')); else next.delete('pickUsers');
    setParams(next);
  }, [params, setParams]);

  const applyClub = useCallback((data) => {
    setClub(data);
    const f = formFromClub(data);
    const m = membershipFromClub(data);
    setForm(f); setInitialForm(f);
    setMembership(m); setInitialMembership(m);
    setIsGlobal(!!data.isGlobal);
    setHomeCity(data.locationCity ? { slug: null, label: data.locationCity, lat: data.latitude ?? null, lng: data.longitude ?? null } : null);
    setLocationTouched(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const r = await clubService.getClub(id);
    if (r.success && r.data) {
      if (r.data.userRole !== 'owner' && r.data.userRole !== 'admin') { navigate(`/clubs/${id}`, { replace: true }); return; }
      applyClub(r.data);
    } else {
      setError(r.error || 'Club not found');
    }
    setLoading(false);
  }, [id, navigate, applyClub]);

  useEffect(() => { load(); }, [load]);

  // The forum deep-links to `/manage#forum-categories` — land on FEATURES and scroll to the panel.
  useEffect(() => {
    if (location.hash === '#forum-categories' && tab !== 'features') setTab('features');
  }, [location.hash, tab, setTab]);
  useEffect(() => {
    if (!club || tab !== 'features' || location.hash !== '#forum-categories') return;
    const el = document.getElementById('forum-categories');
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
  }, [club, tab, location.hash]);

  // Category count for the `CLUB FORUM · ON · 3 CATEGORIES` summary line. Re-read when the
  // owner leaves FEATURES, so edits made inside ForumCategoriesPanel show up in the rail.
  const prevTab = useRef(tab);
  const [categoryRefresh, setCategoryRefresh] = useState(0);
  useEffect(() => {
    if (prevTab.current === 'features' && tab !== 'features') setCategoryRefresh((n) => n + 1);
    prevTab.current = tab;
  }, [tab]);
  useEffect(() => {
    if (!club || !form?.forumEnabled) return undefined;
    let cancelled = false;
    (async () => {
      const r = await clubService.listForumCategories(id);
      if (!cancelled && r.success) setCategoryCount((r.data || []).length);
    })();
    return () => { cancelled = true; };
  }, [club, form?.forumEnabled, id, categoryRefresh]);

  useEffect(() => () => window.clearTimeout(savedTimer.current), []);

  const change = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaveError(null);
    if (saveState === 'saved') setSaveState('idle');
  };
  const changeMembership = (key, value) => {
    setMembership((prev) => ({ ...prev, [key]: value }));
    setSaveError(null);
    if (saveState === 'saved') setSaveState('idle');
  };

  const clubDirty = !!form && (JSON.stringify(form) !== JSON.stringify(initialForm) || locationTouched);
  const membershipDirty = !!membership && JSON.stringify(membership) !== JSON.stringify(initialMembership);
  const dirty = clubDirty || membershipDirty;

  const locked = !!club?.membershipFeeLockedAt;
  const lockedShape = club?.membershipMode || 'free';
  const modeDisabled = (uiMode) => locked && apiShapeFor(uiMode) !== 'free' && apiShapeFor(uiMode) !== lockedShape;

  // ── Uploads (immediate; not part of SAVE CHANGES) ──
  const upload = async (kind, file, ref) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [kind]: true }));
    setSaveError(null);
    const fn = kind === 'avatar' ? clubService.uploadAvatar : clubService.uploadBackground;
    const r = await fn(id, file);
    const key = `${kind}Url`;
    if (r.success && r.data?.[key]) setClub((prev) => ({ ...prev, [key]: r.data[key] }));
    else setSaveError(r.error || `Failed to upload ${kind}`);
    setUploading((u) => ({ ...u, [kind]: false }));
    if (ref.current) ref.current.value = '';
  };
  const removeImage = async (kind) => {
    setSaveError(null);
    const r = await clubService.removeClubImage(id, kind);
    if (r.success) { setClub((prev) => ({ ...prev, [`${kind}Url`]: null })); say(`${kind.toUpperCase()} REMOVED`); }
    else setSaveError(r.error || `Failed to remove ${kind}`);
  };

  // ── Save ──
  const handleSave = async () => {
    if (!form || saveState === 'saving' || !dirty) return;
    if (!form.name.trim()) { setSaveError('Club name is required'); return; }
    if (locationTouched && !isGlobal && !homeCity) { setSaveError('Pick the club’s town, or set it to Global'); return; }
    if (membership.mode !== 'free' && priceCentsOf(membership) <= 0) { setSaveError('Set a price, or make the club free'); return; }
    if (membership.mode === 'one_time' && !membership.lifetime) {
      const t = parseInt(membership.term, 10);
      if (!Number.isFinite(t) || t < 1 || t > 3650) { setSaveError('Term must be between 1 and 3650 days'); return; }
    }
    setSaveState('saving'); setSaveError(null);

    if (clubDirty) {
      const payload = {
        name: form.name.trim(),
        tagline: form.tagline.trim() || null,
        description: form.description.trim() || null,
        ritual: form.ritual.trim() || null,
        isPublic: form.isPublic,
        requiresApproval: form.requiresApproval,
        anyoneCanInvite: form.anyoneCanInvite,
        maxMembers: form.maxMembers ? parseInt(form.maxMembers, 10) : null,
        memberSignalsEnabled: form.memberSignalsEnabled,
        forumEnabled: form.forumEnabled,
        showMembersList: form.showMembersList,
        gatheringsEnabled: form.gatheringsEnabled,
        // Omitted when untouched — a null isGlobal leaves the location alone server-side.
        ...(locationTouched ? {
          isGlobal,
          locationCity: isGlobal ? null : (homeCity?.label || null),
          latitude: isGlobal ? null : (homeCity?.lat ?? null),
          longitude: isGlobal ? null : (homeCity?.lng ?? null),
        } : {}),
      };
      const r = await clubService.updateClub(id, payload);
      if (!r.success) { setSaveError(r.error || 'Failed to save changes'); setSaveState('idle'); return; }
    }
    if (membershipDirty) {
      const r = await clubMembershipService.updateMembershipConfig(id, {
        mode: apiShapeFor(membership.mode),
        priceCents: membership.mode === 'free' ? null : priceCentsOf(membership),
        billingPeriod: apiShapeFor(membership.mode) === 'subscription' ? (membership.mode === 'annual' ? 'annual' : 'monthly') : null,
        termDays: membership.mode === 'one_time' ? (membership.lifetime ? null : parseInt(membership.term, 10)) : null,
        currency: membership.currency,
      });
      if (!r.success) { setSaveError(r.error || 'Failed to save membership'); setSaveState('idle'); return; }
    }
    // Re-read once so the lock state, normalized values and any server-side defaults are what we show.
    const fresh = await clubService.getClub(id);
    if (fresh.success && fresh.data) applyClub(fresh.data);
    setSaveState('saved');
    window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setSaveState('idle'), 3000);
  };

  // ── Danger ──
  const toggleTransfer = async () => {
    if (transferOpen) { setTransferOpen(false); disarm(); return; }
    setTransferOpen(true);
    if (admins === null) {
      const r = await clubService.getMembers(id, 1, 200);
      const list = r.success ? (Array.isArray(r.data) ? r.data : r.data?.items || []) : [];
      setAdmins(list.filter((m) => m.role === 'admin'));
    }
  };
  const transferTo = async (member) => {
    if (!confirm(`xfer-${member.userId}`)) return;
    setTransferBusy(true);
    const r = await clubService.transferOwnership(id, member.userId);
    setTransferBusy(false);
    if (r.success) {
      setTransferOpen(false);
      say(`${String(member.displayName).split(' ')[0].toUpperCase()} NOW OWNS THIS CLUB`);
      navigate(`/clubs/${id}`);
    } else {
      await showAlert({ title: 'TRANSFER FAILED', message: r.error || 'Failed to transfer ownership', type: 'error' });
    }
  };
  const handleDelete = async () => {
    if (!confirm('delete-club')) return;
    setDeleting(true);
    const r = await clubService.deleteClub(id);
    if (r.success) { navigate('/clubs'); return; }
    setDeleting(false);
    setSaveError(r.error || 'Failed to delete club');
  };

  // ── Render ──
  const phone = mobile;
  const frame = (children) => (
    <SoftSurface>
      <SurfacePage bare showTopBarOnMobile>
        <main className="mx-auto flex w-full flex-col font-mono" style={{ maxWidth: 1180, padding: phone ? '12px 12px 24px' : '24px 32px 72px', gap: phone ? 10 : 16, boxSizing: 'border-box' }} data-testid="club-settings">
          {children}
        </main>
      </SurfacePage>
    </SoftSurface>
  );

  if (loading) return frame(<div className="py-24 text-center"><LoadingSpinner label="LOADING…" /></div>);
  if (error || !club || !form) return frame(<div className="py-16"><ErrorState message={error} onRetry={load} /></div>);

  const rows = summaryRows(form, membership, categoryCount);
  const saveLabel = saveState === 'saving' ? 'SAVING…' : saveState === 'saved' ? 'SAVED' : 'SAVE CHANGES';
  const canSave = dirty && saveState !== 'saving';
  const peopleTab = PEOPLE_TABS.includes(tab);
  const wide = !phone;

  const saveButton = (h) => (
    <PillButton variant={saveState === 'saved' ? 'cyan' : 'primary'} size={h} fontSize={12} tracking=".14em" onClick={handleSave} disabled={!canSave} style={{ width: '100%', opacity: canSave || saveState === 'saved' ? 1 : 0.4, cursor: canSave ? 'pointer' : 'not-allowed' }} data-testid="save-button">
      {saveLabel}
    </PillButton>
  );

  const basics = (
    <>
      <ClubPanel phone={phone} style={{ gap: 18 }}>
        <span style={lbl(12, '.16em')}>IDENTITY</span>
        <FieldBlock label="CLUB NAME" required>
          <input type="text" value={form.name} onChange={(e) => change('name', e.target.value)} maxLength={100} placeholder="Name the club" aria-label="Club name" style={{ ...inputStyle, fontSize: 15 }} />
        </FieldBlock>
        <FieldBlock label="TAGLINE · ONE LINE, READ ALOUD" counter={`${form.tagline.length}/80`}>
          <input type="text" value={form.tagline} onChange={(e) => change('tagline', e.target.value)} maxLength={80} placeholder="One short line under the name" aria-label="Tagline" style={{ ...inputStyle, ...voiceStyle }} />
        </FieldBlock>
        <FieldBlock label="WHAT THIS CLUB IS" counter={`${form.description.length}/500`}>
          <textarea value={form.description} onChange={(e) => change('description', e.target.value)} maxLength={500} rows={5} placeholder="The full story. This is the About tab." aria-label="Description" style={textareaStyle} />
        </FieldBlock>
        <div className="grid" style={{ gridTemplateColumns: phone ? 'minmax(0,1fr)' : '120px minmax(0,1fr) minmax(0,1fr)', gap: 12 }}>
          <ImageSlot label="AVATAR" imageUrl={club.avatarUrl} fit="contain" uploading={uploading.avatar} onPick={() => avatarInputRef.current?.click()} onRemove={() => removeImage('avatar')} inputRef={avatarInputRef} onFile={(e) => upload('avatar', e.target.files?.[0], avatarInputRef)} testId="avatar-input" hint="Square. Shows on cards and next to the name." />
          <ImageSlot label="COVER PHOTOS" imageUrl={club.photos?.[0]?.url || club.bannerUrl} replaceLabel="EDIT COVER →" onReplaceLink={`/clubs/${id}#edit-cover`} testId="edit-cover-link" hint="Photos, layout, framing and video are set with EDIT COVER on the club page." />
          <ImageSlot label="BACKGROUND" imageUrl={club.backgroundUrl} uploading={uploading.background} onPick={() => backgroundInputRef.current?.click()} onRemove={() => removeImage('background')} inputRef={backgroundInputRef} onFile={(e) => upload('background', e.target.files?.[0], backgroundInputRef)} testId="background-input" hint="Wallpaper behind the whole club page. Optional." />
        </div>
        <div className="flex flex-col" style={{ gap: 8 }}>
          <span style={fieldLabel}>HOME TOWN</span>
          <ClubLocationField isGlobal={isGlobal} city={homeCity} onChange={(next) => { setIsGlobal(next.isGlobal); setHomeCity(next.city); setLocationTouched(true); setSaveError(null); }} />
          <span style={{ fontSize: 11, lineHeight: 1.55, color: MUTED }}>{isGlobal ? 'Global clubs show up everywhere, but never in a town’s list.' : 'The club shows up in this town’s Clubs list and on its map.'}</span>
        </div>
      </ClubPanel>
      <ClubPanel phone={phone} style={{ gap: 10 }}>
        <span style={lbl(12, '.16em')}>THE RITUAL</span>
        <span style={{ fontSize: 11, lineHeight: 1.55, color: MUTED }}>One line members see on the About tab. Optional.</span>
        <input type="text" value={form.ritual} onChange={(e) => change('ritual', e.target.value)} maxLength={200} placeholder="Last one in the pool buys the first round next time." aria-label="Ritual" style={{ ...inputStyle, ...voiceStyle, height: 52 }} />
      </ClubPanel>
      {emailClient && (
        <ClubPanel phone={phone} style={{ gap: 12 }} data-testid="email-settings-card">
          <span style={lbl(12, '.16em')}>EMAIL</span>
          <span style={{ fontSize: 11, lineHeight: 1.55, color: MUTED }}>Where the club's email comes from and where replies land. Saves on its own. Send from your own domain once its records are in.</span>
          <SendingFrom client={emailClient} />
        </ClubPanel>
      )}
    </>
  );

  const visibilityNote = form.isPublic
    ? 'Anyone can find the club, read the feed and join without asking. Good for a club that wants to grow.'
    : 'People outside see the name, the tagline and the member count. Everything else opens once you let them in.';
  const access = (
    <>
      <ClubPanel phone={phone}>
        <span style={lbl(12, '.16em')}>VISIBILITY</span>
        <div className="grid" style={{ gridTemplateColumns: phone ? 'minmax(0,1fr)' : 'repeat(2, minmax(0,1fr))', gap: 8 }} role="group" aria-label="Visibility">
          {[['PUBLIC', true, 'Anyone can see the club and join on the spot.'], ['PRIVATE', false, 'People see the name and ask. You decide.']].map(([l, val, note]) => {
            const on = form.isPublic === val;
            return (
              <button key={l} type="button" aria-pressed={on} onClick={() => change('isPublic', val)} className="flex flex-col text-left font-mono" style={{ gap: 6, padding: '18px 20px', border: '2px solid transparent', borderRadius: 20, background: on ? 'linear-gradient(#000,#000) padding-box, linear-gradient(90deg,#FD199F,#FF33C6,#B5B1FF,#127BFD,#22ECFB,#FAE1B4,#FCAA6D,#FD199F) border-box' : INSET, color: on ? '#fff' : INK, cursor: 'pointer', transition: 'background-color .2s, color .2s' }}>
                <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: '.12em' }}>{l}</span>
                <span style={{ fontSize: 11, lineHeight: 1.55, opacity: 0.75 }}>{note}</span>
              </button>
            );
          })}
        </div>
        <span style={{ fontSize: 12, lineHeight: 1.6, color: MUTED }}>{visibilityNote}</span>
      </ClubPanel>
      <ClubPanel phone={phone} style={{ gap: 4 }}>
        <span style={{ ...lbl(12, '.16em'), paddingBottom: 8 }}>JOINING</span>
        <SwitchRowSoft rule={false} title="Requests need approval" desc={form.isPublic ? 'Off lets anyone through the moment they ask.' : 'Private clubs always ask. Every request lands with you.'} on={form.isPublic ? form.requiresApproval : true} disabled={!form.isPublic} onChange={(v) => change('requiresApproval', v)} testId="switch-requiresApproval" />
        <SwitchRowSoft title="Anyone can invite" desc="Off means only admins can bring someone in." on={form.anyoneCanInvite} onChange={(v) => change('anyoneCanInvite', v)} testId="switch-anyoneCanInvite" />
        <div className="flex items-center" style={{ gap: 16, padding: '14px 0', borderTop: RULE }}>
          <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>Member cap</span>
            <span style={{ fontSize: 11, lineHeight: 1.5, color: MUTED }}>Requests keep arriving after the cap. You just can’t accept past it.</span>
          </span>
          <input type="number" min={2} value={form.maxMembers} onChange={(e) => change('maxMembers', e.target.value)} placeholder="NONE" aria-label="Member cap" style={{ flexShrink: 0, width: 96, height: 42, padding: '0 12px', border: 'none', borderRadius: 999, background: INSET, textAlign: 'center', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, color: INK, outline: 'none' }} />
        </div>
      </ClubPanel>
    </>
  );

  const featureRows = [
    { key: 'memberSignalsEnabled', label: 'Member Portals', desc: 'Members broadcast a short, expiring Portal to the club.' },
    { key: 'forumEnabled', label: 'Club forum', desc: 'Threaded discussion with categories, separate from the feed.' },
    { key: 'showMembersList', label: 'Show the members list', desc: 'Off hides who is in from everyone but admins.' },
    { key: 'gatheringsEnabled', label: 'Gatherings', desc: 'Group several events into one weekend. Every ticket is its own charge.' },
  ];
  const features = (
    <ClubPanel phone={phone} style={{ gap: 4 }}>
      <span style={{ ...lbl(12, '.16em'), paddingBottom: 8 }}>WHAT THIS CLUB HAS</span>
      {featureRows.map((r, i) => (
        <React.Fragment key={r.key}>
          <SwitchRowSoft rule={i > 0} title={r.label} desc={r.desc} on={form[r.key]} onChange={(v) => change(r.key, v)} testId={`switch-${r.key}`} />
          {r.key === 'forumEnabled' && form.forumEnabled && (
            <div style={{ margin: '0 0 14px', padding: 14, borderRadius: 18, background: INSET }}>
              <ForumCategoriesPanel clubId={id} enabled={form.forumEnabled} />
            </div>
          )}
          {r.key === 'forumEnabled' && !form.forumEnabled && <div className="hidden"><ForumCategoriesPanel clubId={id} enabled={false} /></div>}
          {r.key === 'gatheringsEnabled' && form.gatheringsEnabled && (
            <div style={{ margin: '0 0 14px' }}>
              <PillButton variant="tertiary" size={36} fontSize={10} onClick={() => navigate(`/clubs/${id}/gatherings`)} data-testid="manage-gatherings">MANAGE GATHERINGS →</PillButton>
            </div>
          )}
        </React.Fragment>
      ))}
      <span style={{ paddingTop: 10, borderTop: RULE, fontSize: 11, lineHeight: 1.6, color: MUTED }}>Turning a feature off hides its tab for everyone. Nothing is deleted. Turn it back on and the threads are still there.</span>
    </ClubPanel>
  );

  const paid = membership.mode !== 'free';
  const perks = memberPerks(form);
  const membershipTab = (
    <>
      <ClubPanel phone={phone} style={{ gap: 16 }}>
        <span style={lbl(12, '.16em')}>MEMBERSHIP</span>
        <span className="flex" style={{ gap: 2, padding: 3, borderRadius: 999, background: INSET, overflowX: 'auto', scrollbarWidth: 'none' }} role="group" aria-label="Membership mode">
          {BILLING_MODES.map((b) => {
            const on = membership.mode === b.key;
            const dis = modeDisabled(b.key);
            return <button key={b.key} type="button" aria-pressed={on} disabled={dis} onClick={() => changeMembership('mode', b.key)} style={{ flex: '1 0 auto', height: 42, padding: '0 16px', border: '2px solid transparent', borderRadius: 999, background: on ? 'linear-gradient(#000,#000) padding-box, linear-gradient(90deg,#FD199F,#FF33C6,#B5B1FF,#127BFD,#22ECFB,#FAE1B4,#FCAA6D,#FD199F) border-box' : 'transparent', color: on ? '#fff' : INK, opacity: dis ? 0.4 : 1, fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '.12em', cursor: dis ? 'not-allowed' : 'pointer' }}>{b.label}</button>;
          })}
        </span>
        {locked && (
          <div className="flex flex-col" style={{ gap: 6, padding: '14px 18px', borderRadius: 18, background: '#FEF3C7' }}>
            <span style={lbl(10, '.16em', 800)}>A MEMBER HAS PAID</span>
            <span style={{ fontSize: 12, lineHeight: 1.6, color: BODY }}>The paid shape and currency are locked. You can still change the price, and members keep the price they joined at. Making the club free again cancels active subscriptions.</span>
          </div>
        )}
        {paid && (
          <div className="flex flex-wrap items-end" style={{ gap: 12 }}>
            <label className="flex flex-col" style={{ gap: 8 }}>
              <span style={fieldLabel}>PRICE</span>
              <span className="flex items-center" style={{ height: 52, padding: '0 20px', borderRadius: 999, background: INSET }}>
                <span style={{ fontSize: 18, fontWeight: 800 }}>$</span>
                <input type="number" min="0" step="0.01" value={membership.price} onChange={(e) => changeMembership('price', e.target.value)} placeholder="0.00" aria-label="Price" style={{ width: 110, border: 'none', background: 'none', fontFamily: 'inherit', fontSize: 18, fontWeight: 800, color: INK, padding: '0 4px', outline: 'none' }} />
              </span>
            </label>
            <div className="flex flex-col" style={{ gap: 8 }}>
              <span style={fieldLabel}>CURRENCY</span>
              <span className="inline-flex" style={{ gap: 2, padding: 3, borderRadius: 999, background: INSET }} role="radiogroup" aria-label="Currency">
                {['CAD', 'USD'].map((c) => <button key={c} type="button" role="radio" aria-checked={membership.currency === c} disabled={locked} onClick={() => !locked && changeMembership('currency', c)} style={{ height: 44, padding: '0 16px', border: 'none', borderRadius: 999, background: membership.currency === c ? INK : 'transparent', color: membership.currency === c ? '#fff' : INK, fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '.12em', cursor: locked ? 'not-allowed' : 'pointer', opacity: locked && membership.currency !== c ? 0.5 : 1 }}>{c}</button>)}
              </span>
            </div>
            {membership.mode === 'one_time' && (
              <>
                <span className="flex items-center" style={{ gap: 10, height: 52, padding: '0 8px 0 18px', borderRadius: 999, background: INSET }}>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>Lifetime</span>
                  <Switch on={membership.lifetime} onChange={(v) => changeMembership('lifetime', v)} label="Lifetime membership" />
                </span>
                {!membership.lifetime && (
                  <label className="flex flex-col" style={{ gap: 8 }}>
                    <span style={fieldLabel}>TERM · DAYS</span>
                    <input type="number" min="1" max="3650" value={membership.term} onChange={(e) => changeMembership('term', e.target.value)} aria-label="Term in days" style={{ ...inputStyle, width: 110, height: 52, fontSize: 15, fontWeight: 700 }} />
                  </label>
                )}
              </>
            )}
          </div>
        )}
        <p className="m-0" style={{ fontSize: 12, lineHeight: 1.65, color: BODY }} data-testid="fee-note">{feeNote(membership)}</p>
      </ClubPanel>
      <ClubPanel phone={phone} style={{ gap: 10 }}>
        <PanelHead title="WHAT MEMBERS GET" right="BUILT FROM FEATURES" />
        {perks.map((p) => (
          <span key={p} className="flex items-center" style={{ gap: 12, padding: '12px 16px', borderRadius: 16, background: INSET, fontSize: 13 }}>
            <span className="flex flex-none items-center justify-center" style={{ width: 20, height: 20, borderRadius: '50%', background: CYAN }}><svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="square" aria-hidden="true"><path d="M2 6 L5 9 L10 3" /></svg></span>
            {p}
          </span>
        ))}
        <span style={{ fontSize: 11, lineHeight: 1.55, color: MUTED }}>Switch something on or off under FEATURES and this list follows.</span>
      </ClubPanel>
    </>
  );

  const deleteArmed = armed === 'delete-club';
  const danger = isOwner && (
    <>
      <ClubPanel phone={phone}>
        <span style={lbl(12, '.16em')}>HAND IT OVER</span>
        <span className="flex flex-wrap items-center justify-between" style={{ gap: 12 }}>
          <span style={{ flex: '1 1 280px', fontSize: 12, lineHeight: 1.6, color: MUTED }}>Make another admin the owner. Memberships, revenue and payouts move with the title. You stay on as an admin.</span>
          <PillButton variant={transferOpen ? 'primary' : 'secondary'} size={42} onClick={toggleTransfer} data-testid="transfer-toggle">{transferOpen ? 'CLOSE' : 'TRANSFER OWNERSHIP'}</PillButton>
        </span>
        {transferOpen && (
          <div className="flex flex-col" style={{ gap: 6, padding: 12, borderRadius: 18, background: INSET }} data-testid="transfer-list">
            <span style={{ ...lbl(10, '.16em'), padding: '4px 6px 6px' }}>PICK THE ADMIN WHO TAKES THE TITLE</span>
            {admins === null && <div className="py-4 text-center"><LoadingSpinner size="sm" /></div>}
            {admins !== null && admins.length === 0 && <span style={{ padding: '6px 6px 10px', fontSize: 12, lineHeight: 1.6, color: MUTED }}>No admins yet. Make someone an admin from the MEMBERS tab first. Only an admin can take the title.</span>}
            {admins?.map((m) => {
              const on = armed === `xfer-${m.userId}`;
              return (
                <div key={m.userId} className="flex items-center" style={{ gap: 12, padding: 8, borderRadius: 999, background: '#fff' }}>
                  <Face src={m.avatarUrl} name={m.displayName} size={36} />
                  <span className="min-w-0 flex-1 truncate" style={{ fontSize: 13, fontWeight: 700 }}>{m.displayName}</span>
                  <PillButton variant={on ? 'armed' : 'primary'} size={34} disabled={transferBusy} onClick={() => transferTo(m)} aria-label={`Hand it over to ${m.displayName}`}>{on ? `CONFIRM · ${String(m.displayName).split(' ')[0].toUpperCase()} OWNS IT` : 'HAND IT OVER'}</PillButton>
                </div>
              );
            })}
          </div>
        )}
      </ClubPanel>
      <ClubPanel phone={phone} style={{ boxShadow: `inset 0 0 0 2px ${RED}` }} data-testid="delete-panel">
        <span style={{ ...lbl(12, '.16em'), color: RED }}>DELETE THIS CLUB</span>
        <span style={{ fontSize: 12, lineHeight: 1.6, color: BODY }}>
          {deleteArmed
            ? `"${club.name}" comes off Portal now. Members, posts, events and your contact list are all hidden. You have 30 days to bring it back from your Clubs page. After that it is gone for good.`
            : 'Deleting the club removes the feed, the forum and every membership. Linked events stay, unlinked.'}
        </span>
        <span className="flex flex-wrap" style={{ gap: 6 }}>
          <PillButton variant="armed" size={46} onClick={handleDelete} disabled={deleting} data-testid="delete-club">{deleting ? 'DELETING…' : deleteArmed ? 'YES, DELETE IT' : 'DELETE THIS CLUB'}</PillButton>
          {deleteArmed && <PillButton variant="tertiary" size={46} weight={700} onClick={disarm}>KEEP IT</PillButton>}
        </span>
      </ClubPanel>
    </>
  );

  const people = audienceOwner && <ContactsPanel owner={audienceOwner} mobile={phone} onSendTo={sendTo} />;
  const send = audienceOwner && <BroadcastsPanel owner={audienceOwner} perms={PAGE_PERMS} mobile={phone} initialPick={initialPick} />;
  const body = tab === 'basics' ? basics : tab === 'access' ? access : tab === 'features' ? features : tab === 'membership' ? membershipTab : tab === 'people' ? people : tab === 'send' ? send : danger;

  const tabRow = !(peopleTab && sendTakeover) && (
    <div role="tablist" aria-label="Club settings" className="flex" style={{ flexWrap: phone ? 'nowrap' : 'wrap', gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }} data-testid="settings-tabs">
      {tabs.map((t) => {
        const on = t.key === tab;
        return (
          <RingTab key={t.key} role="tab" aria-selected={on} on={on && !t.danger} onClick={() => setTab(t.key)} style={t.danger ? { background: on ? RED : '#fff', color: on ? '#fff' : RED } : undefined} data-testid={`tab-${t.key}`}>
            {t.label}
          </RingTab>
        );
      })}
    </div>
  );

  return frame(
    <>
      <div className="flex flex-wrap items-center justify-between" style={{ gap: 10 }}>
        <ClubBackPill to={`/clubs/${id}`}>{club.name}</ClubBackPill>
        <TagPill bg={isOwner ? CYAN : '#fff'} fg={INK} size={10} padding="6px 12px" style={{ letterSpacing: '.16em' }} data-testid="role-pill">{isOwner ? 'OWNER' : 'ADMIN'}</TagPill>
      </div>
      <section className="flex items-center bg-white" style={{ gap: 18, padding: phone ? 14 : '18px 22px', borderRadius: phone ? 22 : 24 }} data-testid="settings-header">
        <ClubLogo club={club} size={phone ? 56 : 72} radius={18} fontSize={phone ? 24 : 30} />
        <span className="flex min-w-0 flex-col" style={{ gap: 6 }}>
          <span style={{ ...lbl(11, '.18em'), color: MUTED }}>CLUB SETTINGS</span>
          <h1 className="m-0" style={{ fontSize: phone ? 22 : 30, fontWeight: 700, lineHeight: 1.1, textTransform: 'none', overflowWrap: 'anywhere' }}>{form.name || 'Untitled club'}</h1>
        </span>
      </section>
      {tabRow}
      <div className="grid items-start" style={{ gridTemplateColumns: wide && !peopleTab ? 'minmax(0,1fr) 340px' : 'minmax(0,1fr)', gap: phone ? 10 : 16 }}>
        <div className="flex min-w-0 flex-col" style={{ gap: phone ? 10 : 16 }}>
          {saveError && <ErrorBand title="COULD NOT SAVE" testId="save-error">{saveError}</ErrorBand>}
          {body}
        </div>
        {wide && !peopleTab && (
          <div className="flex min-w-0 flex-col" style={{ gap: 10, position: 'sticky', top: 88 }} data-testid="settings-rail">
            <WhatMembersSee rows={rows} dirty={dirty} />
            {saveButton(54)}
            <PillButton variant="ghost" size={44} weight={700} to={`/clubs/${id}`} style={{ width: '100%' }}>PREVIEW AS MEMBER</PillButton>
          </div>
        )}
      </div>
      {phone && !peopleTab && (
        <>
          <WhatMembersSee rows={rows} dirty={dirty} phone />
          <div className="sticky" style={{ bottom: safeBottom(78), zIndex: 39, padding: '0 0 8px' }} data-testid="save-bar">{saveButton(52)}</div>
        </>
      )}
      <Toast text={toast} phone={phone} />
    </>,
  );
}
